import React, { useState } from "react";
import { X, Shield, Copy, Check, AlertTriangle, Search } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

/**
 * WhistleblowerModal — anonymous tip drop.
 *
 * Two tabs: SUBMIT a new tip, or CHECK the status of an existing tip via
 * the 8-char receipt code. Submission requires no account, captures no
 * IP or user-agent (the backend strips them at write time).
 */
export default function WhistleblowerModal({ open, onClose }) {
  const [tab, setTab] = useState("submit"); // 'submit' | 'check'
  // submit form
  const [kind, setKind] = useState("tip"); // 'tip' | 'bug' | 'idea'
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [contactHint, setContactHint] = useState("");
  const [busy, setBusy] = useState(false);
  const [receipt, setReceipt] = useState("");
  const [copied, setCopied] = useState(false);
  // check form
  const [checkCode, setCheckCode] = useState("");
  const [checkResult, setCheckResult] = useState(null);
  const [checkErr, setCheckErr] = useState("");
  // follow-up reply (tipster → admin)
  const [followUp, setFollowUp] = useState("");

  if (!open) return null;

  const reset = () => {
    setKind("tip");
    setSubject("");
    setBody("");
    setContactHint("");
    setReceipt("");
    setCopied(false);
    setCheckCode("");
    setCheckResult(null);
    setCheckErr("");
    setFollowUp("");
  };

  const close = () => {
    reset();
    onClose?.();
  };

  const submit = async () => {
    if (busy) return;
    if (subject.trim().length < 3) {
      toast.error("Subject must be at least 3 characters");
      return;
    }
    if (body.trim().length < 10) {
      toast.error("Tip body must be at least 10 characters");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/whistleblower/drop/submit", {
        subject: subject.trim(),
        body: body.trim(),
        contact_hint: contactHint.trim() || null,
        kind,
      });
      setReceipt(r.data?.receipt_code || "");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to submit. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const copyReceipt = async () => {
    try {
      await navigator.clipboard.writeText(receipt);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* ignore */ }
  };

  const checkStatus = async () => {
    const code = checkCode.trim().toUpperCase();
    setCheckErr("");
    setCheckResult(null);
    if (code.length !== 8) {
      setCheckErr("Receipt code is 8 characters.");
      return;
    }
    setBusy(true);
    try {
      const r = await api.get(`/whistleblower/drop/status/${encodeURIComponent(code)}`);
      setCheckResult(r.data);
    } catch (err) {
      setCheckErr(err?.response?.data?.detail || "Lookup failed.");
    } finally {
      setBusy(false);
    }
  };

  const sendFollowUp = async () => {
    const code = checkCode.trim().toUpperCase();
    if (!followUp.trim() || code.length !== 8 || busy) return;
    setBusy(true);
    try {
      await api.post(`/whistleblower/drop/follow-up/${encodeURIComponent(code)}`, {
        text: followUp.trim(),
      });
      setFollowUp("");
      // Reload the conversation so the new message shows up.
      const r = await api.get(`/whistleblower/drop/status/${encodeURIComponent(code)}`);
      setCheckResult(r.data);
      toast.success("Follow-up sent");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send follow-up");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[200] bg-black/85 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4"
      data-testid="whistleblower-modal"
      onClick={(e) => { if (e.target === e.currentTarget) close(); }}
    >
      <div className="w-full max-w-xl bg-[#0a0a0a] border border-[#ff3b30]/40 shadow-[0_0_60px_rgba(255,59,48,0.15)] relative flex flex-col max-h-[90vh]">
        <button
          type="button"
          onClick={close}
          className="absolute top-3 right-3 text-white/50 hover:text-white z-10"
          data-testid="whistleblower-close"
          aria-label="Close"
        >
          <X size={18} />
        </button>

        {/* Header — sticky so it stays visible when the body scrolls */}
        <div className="px-5 pt-5 pb-3 border-b border-white/10 flex items-center gap-3 shrink-0">
          <div className="w-9 h-9 border border-[#ff3b30] flex items-center justify-center shrink-0">
            <Shield size={18} className="text-[#ff3b30]" />
          </div>
          <div className="min-w-0">
            <div className="font-heading font-black uppercase tracking-tight text-white text-lg leading-tight">
              Tips &amp; Issues
            </div>
            <div className="font-mono text-[10px] tracking-[0.22em] text-zinc-500 uppercase truncate">
              Anonymous · No IP logged · No account required
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-white/10">
          {[
            { id: "submit", label: "Drop Something" },
            { id: "check", label: "Check Status" },
          ].map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              data-testid={`whistleblower-tab-${t.id}`}
              className={`flex-1 px-4 py-3 font-mono text-[11px] uppercase tracking-[0.22em] transition-colors ${
                tab === t.id
                  ? "text-[#ff3b30] border-b-2 border-[#ff3b30]"
                  : "text-zinc-500 hover:text-zinc-300"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="px-5 py-4 space-y-3 overflow-y-auto flex-1 min-h-0">
          {tab === "submit" && !receipt && (
            <>
              <div className="bg-amber-500/[0.06] border border-amber-500/30 px-3 py-2 flex items-start gap-2">
                <AlertTriangle size={14} className="text-amber-500 mt-0.5 shrink-0" />
                <p className="font-mono text-[11px] text-amber-100/80 leading-relaxed">
                  We do not log your IP, user-agent, or referrer. After
                  submission you receive a one-time receipt code. Save it.
                  We cannot recover it for you.
                </p>
              </div>

              <div>
                <label className="block font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-1">
                  What is this?
                </label>
                <div className="grid grid-cols-3 gap-2" data-testid="whistleblower-kind">
                  {[
                    { id: "tip",  label: "Tip / Lead" },
                    { id: "bug",  label: "Bug / Issue" },
                    { id: "idea", label: "Feature Idea" },
                  ].map((k) => (
                    <button
                      key={k.id}
                      type="button"
                      onClick={() => setKind(k.id)}
                      data-testid={`whistleblower-kind-${k.id}`}
                      className={`px-2 py-2 border-2 font-mono text-[10px] tracking-[0.18em] uppercase transition-colors ${
                        kind === k.id
                          ? "border-[#ff3b30] text-[#ff3b30] bg-[#ff3b30]/10"
                          : "border-white/15 text-zinc-400 hover:border-white/30"
                      }`}
                    >
                      {k.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-1">
                  Subject *
                </label>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  data-testid="whistleblower-subject"
                  maxLength={200}
                  placeholder={
                    kind === "bug"  ? "Short headline (e.g. 'Vault Card image won't load on mobile')" :
                    kind === "idea" ? "Short headline (e.g. 'Add dark mode for Reading View')" :
                                      "Short headline (e.g. 'Hidden manifest for flight 800')"
                  }
                  className="w-full bg-black border border-white/20 px-3 py-2 text-sm text-white font-mono focus:border-[#ff3b30] outline-none"
                />
              </div>

              <div>
                <label className="block font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-1">
                  {kind === "bug" ? "Bug details *" : kind === "idea" ? "Idea details *" : "Tip body *"}
                </label>
                <textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  data-testid="whistleblower-body"
                  maxLength={10000}
                  rows={5}
                  placeholder={
                    kind === "bug"  ? "What broke? What page? What did you click? Screenshot helps if you can host one and link it." :
                    kind === "idea" ? "What would you build, and where would it live? The more concrete, the better." :
                                      "What do you know? Names, dates, links, locations. The more detail, the better."
                  }
                  className="w-full bg-black border border-white/20 px-3 py-2 text-sm text-white font-mono focus:border-[#ff3b30] outline-none leading-relaxed resize-y min-h-[120px] max-h-[280px]"
                />
                <div className="text-right font-mono text-[9px] text-zinc-600 mt-1">
                  {body.length} / 10000
                </div>
              </div>

              <div>
                <label className="block font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-1">
                  Contact channel (optional)
                </label>
                <input
                  type="text"
                  value={contactHint}
                  onChange={(e) => setContactHint(e.target.value)}
                  data-testid="whistleblower-contact"
                  maxLength={200}
                  placeholder="Burner email, Signal #, Proton — only if you want a reply"
                  className="w-full bg-black border border-white/20 px-3 py-2 text-sm text-white font-mono focus:border-[#ff3b30] outline-none"
                />
              </div>

              <button
                type="button"
                onClick={submit}
                disabled={busy}
                data-testid="whistleblower-submit"
                className="w-full px-4 py-3 border-2 border-[#ff3b30] bg-[#ff3b30]/[0.06] text-[#ff3b30] hover:bg-[#ff3b30] hover:text-black font-mono text-[12px] uppercase tracking-[0.22em] font-bold transition-colors disabled:opacity-50"
              >
                {busy ? "Transmitting…" : "Drop Tip Anonymously"}
              </button>
            </>
          )}

          {tab === "submit" && receipt && (
            <div className="space-y-4" data-testid="whistleblower-receipt">
              <div className="bg-emerald-500/[0.07] border border-emerald-500/40 px-4 py-3">
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-emerald-400 mb-1">
                  Tip Received
                </div>
                <p className="font-mono text-[11px] text-emerald-100/85 leading-relaxed">
                  Your receipt code is below. Save it now. This is the only
                  way to look up the status of your tip.
                </p>
              </div>

              <div className="border border-[#ff3b30]/40 bg-black px-4 py-5 text-center">
                <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-zinc-500 mb-2">
                  Receipt Code
                </div>
                <div className="font-mono text-3xl font-bold text-[#ff3b30] tracking-[0.4em]" data-testid="whistleblower-receipt-code">
                  {receipt}
                </div>
                <button
                  type="button"
                  onClick={copyReceipt}
                  data-testid="whistleblower-receipt-copy"
                  className="mt-4 inline-flex items-center gap-2 px-4 py-2 border border-white/20 text-zinc-300 hover:border-white/40 hover:text-white font-mono text-[10px] uppercase tracking-[0.22em]"
                >
                  {copied ? <><Check size={12} className="text-emerald-400" /> Copied</> : <><Copy size={12} /> Copy code</>}
                </button>
              </div>

              <button
                type="button"
                onClick={() => { reset(); }}
                className="w-full px-4 py-2 border border-white/20 text-zinc-400 hover:border-white/40 hover:text-white font-mono text-[10px] uppercase tracking-[0.22em]"
              >
                Submit another tip
              </button>
            </div>
          )}

          {tab === "check" && (
            <>
              <p className="font-mono text-[11px] text-zinc-400 leading-relaxed">
                Enter your 8-character receipt code to see the current status
                and any public reply from the curators.
              </p>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={checkCode}
                  onChange={(e) => setCheckCode(e.target.value.toUpperCase())}
                  data-testid="whistleblower-check-code"
                  maxLength={8}
                  placeholder="ABCD2345"
                  className="flex-1 bg-black border border-white/20 px-3 py-2 text-base text-[#ff3b30] font-mono tracking-[0.3em] uppercase focus:border-[#ff3b30] outline-none"
                />
                <button
                  type="button"
                  onClick={checkStatus}
                  disabled={busy}
                  data-testid="whistleblower-check-submit"
                  className="px-4 py-2 border-2 border-[#ff3b30] text-[#ff3b30] hover:bg-[#ff3b30] hover:text-black font-mono text-[11px] uppercase tracking-[0.22em] disabled:opacity-50 inline-flex items-center gap-2"
                >
                  <Search size={12} /> Look up
                </button>
              </div>
              {checkErr ? (
                <div className="bg-red-500/[0.06] border border-red-500/40 px-3 py-2 font-mono text-[11px] text-red-300" data-testid="whistleblower-check-error">
                  {checkErr}
                </div>
              ) : null}
              {checkResult ? (
                <div className="border border-white/15 p-4 space-y-3" data-testid="whistleblower-check-result">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500">
                      {checkResult.subject || "your drop"}
                    </div>
                    <div className="font-mono text-[10px] font-bold text-[#ff3b30] uppercase tracking-widest">
                      {checkResult.status}
                    </div>
                  </div>
                  {/* Conversation thread — tipster bubbles red on the left, curator bubbles blue on the right. */}
                  <div className="max-h-[280px] overflow-y-auto space-y-2 bg-black/40 border border-white/10 p-3">
                    {(checkResult.messages || []).length === 0 ? (
                      <div className="font-mono text-[10px] text-zinc-600 text-center py-4">
                        // EMPTY THREAD
                      </div>
                    ) : (
                      checkResult.messages.map((m, i) => {
                        const isAdmin = m.from === "admin";
                        return (
                          <div key={i} className={`flex ${isAdmin ? "justify-end" : "justify-start"}`}>
                            <div
                              className={`max-w-[80%] p-2 font-mono text-[11px] leading-relaxed whitespace-pre-wrap border ${
                                isAdmin
                                  ? "bg-[#0a3a8a]/60 border-[#3b82f6] text-white"
                                  : "bg-[#3a0a0a]/60 border-[#ff3b30] text-white"
                              }`}
                            >
                              <div className={`font-mono text-[8px] tracking-[0.25em] mb-1 ${isAdmin ? "text-blue-300" : "text-red-300"}`}>
                                {isAdmin ? "// CURATOR" : "// YOU"} · {new Date(m.at).toLocaleString()}
                              </div>
                              {m.text}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                  {/* Follow-up reply input — same receipt code, no auth. */}
                  <div className="flex items-end gap-2">
                    <textarea
                      value={followUp}
                      onChange={(e) => setFollowUp(e.target.value)}
                      data-testid="whistleblower-followup-input"
                      maxLength={10000}
                      rows={2}
                      placeholder="Send a follow-up. Stays anonymous."
                      className="flex-1 bg-black border border-white/20 px-3 py-2 text-white font-mono text-[12px] focus:border-[#ff3b30] outline-none resize-y min-h-[56px] max-h-[140px]"
                    />
                    <button
                      type="button"
                      onClick={sendFollowUp}
                      disabled={busy || !followUp.trim()}
                      data-testid="whistleblower-followup-send"
                      className="px-3 py-2.5 border-2 border-[#ff3b30] text-[#ff3b30] hover:bg-[#ff3b30] hover:text-black font-mono text-[10px] uppercase tracking-[0.2em] disabled:opacity-40"
                    >
                      Send
                    </button>
                  </div>
                </div>
              ) : null}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
